import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject } from '@angular/core';
import { MgsFieldDefaultComponent } from './mgs-field-default.component';
import { MatCheckbox, MatCheckboxChange } from '@angular/material/checkbox';
import { ATTRIBUT_TYPES, IAttributTabelle, WERT_DELIMITER, WERT_SUB_DELIMITER } from '@mng/mgs-lib';
import { DIALOG_RESULT_CANCEL, MngDialogService } from '@mng/mng-inputs';
import { NgStyle } from '@angular/common';
import { MatIcon } from '@angular/material/icon';

const JAHR_REGEX = /^\d{4}$/;

@Component({
  selector: 'mng-mgs-field-multilist-component',
  imports: [MatCheckbox, NgStyle, MatIcon],
  template: `
    <div class="group-container">
      @for (tabelleItem of attribut.attributTabelleList; track tabelleItem.id) {
        <div>
          <mat-checkbox
            [checked]="checkedValues[tabelleItem.id]"
            [disabled]="formControl.disabled"
            [ngStyle]="style"
            color="accent"
            [attr.id]="tabelleItem.id"
            (change)="onChange($event, tabelleItem)"
            labelPosition="after"
          >
            <span class="text" style="font-size: small;">{{ tabelleItem.label }}</span>

            @if (mitJahrInfo && istItemAusgewaehlt(tabelleItem)) {
              <span class="text" style="padding-left: 8px; padding-right: 8px;">
                [
                <span
                  class="text-accent"
                  style="padding-left: 4px; padding-right: 4px; cursor: pointer; text-decoration: underline;
"
                >
                  <span (click)="jahrInfoAendern($event, tabelleItem)">
                    {{ jahrInfo[tabelleItem.id] ?? 'Jahr/Lokalisation...' }}
                  </span>
                  @if (jahrInfo[tabelleItem.id]) {
                    <span style="padding-top: 2px;">
                      <mat-icon
                        svgIcon="cancel"
                        style="height: 15px; width: 15px;"
                        color="primary"
                        (click)="jahrInfoLoeschen($event, tabelleItem)"
                      />
                    </span>
                  }
                </span>
                ]
              </span>
            }
          </mat-checkbox>
        </div>
      }
    </div>
  `,
  styleUrl: './mgs-field-list.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MgsFieldMultilistComponent extends MgsFieldDefaultComponent {
  private dialogService = inject(MngDialogService);
  private cdr = inject(ChangeDetectorRef);

  checkedValues: { [tabelleId: number]: boolean } = {};

  mitJahrInfo = false;

  jahrInfo: { [tabelleId: number]: string };
  attributSet() {
    if (ATTRIBUT_TYPES.TYPE_MULTILISTYEAR === this.uiAttribut.attribut.definition.attributTypeId) {
      this.mitJahrInfo = true;
      this.jahrInfo = {};
    }
    super.attributSet();
    this.parseWert();
  }

  parseWert() {
    const checkedValues = {};
    if (this.attribut.wert) {
      // Werte sind mit ';' in einem string ("wert") gespeichert
      const values = this.attribut.wert.split(WERT_DELIMITER);
      values.forEach((value) => {
        let realValue = value;
        if (this.mitJahrInfo) {
          let aktuelleJahrInfo: string | undefined;
          [realValue, aktuelleJahrInfo] = value.split(WERT_SUB_DELIMITER);
          if (aktuelleJahrInfo) {
            this.jahrInfo[+realValue] = aktuelleJahrInfo;
          }
        }
        checkedValues[+realValue] = true;
      });
    }
    this.checkedValues = checkedValues;
  }

  onChange(change: MatCheckboxChange, tabelleItem: IAttributTabelle) {
    if (change.checked) {
      this.checkedValues[+tabelleItem.id] = true;
    } else {
      delete this.checkedValues[tabelleItem.id];
    }

    this.setFormControlValue();
  }

  istItemAusgewaehlt(tabelleItem: IAttributTabelle) {
    // console.log('istItemAusgewaehlt', tabelleItem, this.checkedValues, tabelleItem.id, this.checkedValues[tabelleItem.id]);
    return this.checkedValues[tabelleItem.id];
  }

  jahrInfoAendern(event: MouseEvent, tabelleItem: IAttributTabelle) {
    event.preventDefault();
    event.stopPropagation();

    this.dialogService
      .showTextInput({
        title: tabelleItem.label,
        textInput: this.jahrInfo[tabelleItem.id],
        iconName: 'mode_edit',
        htmlContent: 'Geben Sie bitte das <strong>Jahr</strong> ein...',
        autoselect: true
      })
      .afterClosed()
      .subscribe((result) => {
        if (result !== DIALOG_RESULT_CANCEL) {
          if (result && !JAHR_REGEX.test(result)) {
            this.dialogService.showOK({
              htmlContent: `Die Eingabe muss im Format <strong>JJJJ</strong> erfolgen (z.B. 2024)`
            });
            return;
          }

          this.jahrInfo[tabelleItem.id] = result;
          this.setFormControlValue();
          this.cdr.markForCheck();
        }
      });
  }

  setFormControlValue() {
    let werte = Object.keys(this.checkedValues);

    if (this.mitJahrInfo) {
      werte = werte.map((wert) => {
        const jahrInfo = this.jahrInfo[wert];
        return jahrInfo ? `${wert}${WERT_SUB_DELIMITER}${jahrInfo}` : wert;
      });
    }

    const wertAsString = werte.join(WERT_DELIMITER);
    this.formControl.setValue(wertAsString);
    this.formControl.markAsDirty({ onlySelf: false });
  }

  jahrInfoLoeschen(event: MouseEvent, tabelleItem: IAttributTabelle) {
    event.preventDefault();
    event.stopPropagation();
    delete this.jahrInfo[tabelleItem.id];
  }
}
